import React from 'react';
import { Clock } from 'lucide-react';
import { daysUntil } from '../../lib/developerApi';

/** A scope as a chip: writes in violet, deletes in red, reads neutral. */
export function ScopeChip({ scope }) {
    const tone = scope.endsWith(':delete')
        ? 'bg-red-50 text-red-700'
        : scope.endsWith(':read')
            ? 'bg-stone-100 text-stone-600'
            : 'bg-violet-50 text-violet-700';
    return (
        <span className={`inline-flex items-center font-mono text-[11px] font-medium rounded px-1.5 py-0.5 mr-1 my-0.5 whitespace-nowrap ${tone}`}>
            {scope}
        </span>
    );
}

export function ScopeChips({ scopes }) {
    return <span>{(scopes || []).map((s) => <ScopeChip key={s} scope={s} />)}</span>;
}

export function KeyStatusPill({ apiKey }) {
    if (apiKey.status === 'revoked') {
        return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-stone-100 text-stone-500">Revoked</span>;
    }
    if (apiKey.status === 'expired') {
        return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700">Expired</span>;
    }
    const days = daysUntil(apiKey.expires_at);
    if (days != null && days <= 14) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
                <Clock className="w-3 h-3" />
                Expires in {days} d
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Active
        </span>
    );
}

export function StatTile({ label, value, hint, icon: Icon, tone = 'default', children }) {
    const warn = tone === 'warn';
    return (
        <div className={`rounded-xl border p-4 ${warn ? 'bg-amber-50 border-amber-200' : 'bg-white border-stone-200'}`}>
            <div className={`flex items-center justify-between text-xs font-medium ${warn ? 'text-amber-700' : 'text-stone-500'}`}>
                {label}
                {Icon && <Icon className={`w-4 h-4 ${warn ? 'text-amber-700' : 'text-stone-400'}`} />}
            </div>
            <div className="text-2xl font-semibold text-stone-900 mt-1 tabular-nums">{value}</div>
            {hint && <div className={`text-xs ${warn ? 'text-amber-700' : 'text-stone-400'}`}>{hint}</div>}
            {children}
        </div>
    );
}

/**
 * A request-log time: the time of day for today's rows, with the date for older
 * ones (the log spans up to 30 days, so a bare time is ambiguous).
 */
export function formatLogTime(iso) {
    const d = iso ? new Date(iso) : null;
    if (!d || Number.isNaN(d.getTime())) return '—';
    const sameDay = d.toDateString() === new Date().toDateString();
    return sameDay
        ? d.toLocaleTimeString()
        : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString()}`;
}

export function StatusCode({ status }) {
    const tone = status >= 500
        ? 'bg-red-100 text-red-700'
        : status >= 400
            ? 'bg-amber-100 text-amber-700'
            : 'bg-emerald-50 text-emerald-700';
    return <span className={`font-mono text-[11.5px] font-semibold px-1.5 py-0.5 rounded ${tone}`}>{status}</span>;
}

const METHOD_TONE = {
    GET: 'text-emerald-700',
    POST: 'text-violet-700',
    PATCH: 'text-amber-700',
    PUT: 'text-amber-700',
    DELETE: 'text-red-700',
};

export function Method({ method }) {
    return <span className={`font-mono text-[11px] font-semibold inline-block min-w-[48px] ${METHOD_TONE[method] || 'text-stone-600'}`}>{method}</span>;
}

export function Note({ icon: Icon, title, children }) {
    return (
        <div className="flex items-start gap-3 rounded-lg border border-stone-200 bg-stone-50 px-3.5 py-3">
            {Icon && <Icon className="w-4 h-4 text-stone-500 mt-0.5 shrink-0" />}
            <div>
                <b className="block text-[13px] font-semibold text-stone-900">{title}</b>
                <p className="text-[12.5px] text-stone-500 mt-0.5 leading-relaxed">{children}</p>
            </div>
        </div>
    );
}
